import React, { useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import UploadFileIcon from "assets/upload-file.svg";

type Props = {
  open: boolean;
  onClose: () => void;
  onAudioUploaded?: (file: File) => void;
  onRecordRequested?: () => void;
};

export const AudioSourceModal: React.FC<Props> = ({
  open,
  onClose,
  onAudioUploaded,
  onRecordRequested,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!open) return null;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      event.target.value = "";
      return;
    }
    onAudioUploaded?.(file);
    onClose();
  };

  const handleRecordClick = () => {
    onRecordRequested?.();
  };

  const MicIcon: React.FC = () => (
    <svg viewBox="0 0 24 24">
      <path
        d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zm-5 8a1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V19h-2a1 1 0 0 0 0 2h6a1 1 0 1 0 0-2h-2v-2.07A7 7 0 0 0 19 10a1 1 0 0 0-2 0 5 5 0 0 1-10 0z"
        fill="currentColor"
      />
    </svg>
  );

  const content = (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <OptionsRow>
          <OptionButton type="button" onClick={handleUploadClick}>
            <UploadFileIcon />
            <span>Upload</span>
          </OptionButton>
          <OptionButton type="button" onClick={handleRecordClick}>
            <MicIcon />
            <span>Record</span>
          </OptionButton>
        </OptionsRow>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          onChange={handleFileChange}
        />
      </Dialog>
    </Overlay>
  );

  if (typeof document === "undefined") {
    return content;
  }

  return createPortal(content, document.body);
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 360px;
  max-width: calc(100% - 40px);
  border-radius: 16px;
  background: #ffffff;
  padding: 20px 24px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  text-align: center;
`;

const OptionsRow = styled.div`
  display: flex;
  gap: 12px;
`;

const OptionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover {
    background: #e5e7eb;
    transform: translateY(-1px);
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

export default AudioSourceModal;


